import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-enterprise';
import { AgGridReact, type CustomStatusPanelProps } from 'ag-grid-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  agGridContainerStyles,
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { colors } from '../theme/colors';
import { customerReportService, type GroupReportEmployee } from '../services/customerReportService';
import { useAuth } from '../contexts/AuthContext';
import { GroupReportPreview } from './GroupReportPreview';

const StatusBarComponent: React.FC<CustomStatusPanelProps & { statusPanelParams?: { includeTravelTime?: boolean }; includeTravelTime?: boolean }> = (props) => {
  const [selectedTurnover, setSelectedTurnover] = useState(0);
  const [selectedBillableHours, setSelectedBillableHours] = useState(0);
  const [selectedTravelTime, setSelectedTravelTime] = useState(0);
  const [showTravelTime, setShowTravelTime] = useState(false);
  const includeTravelTime = props.includeTravelTime ?? props.statusPanelParams?.includeTravelTime ?? false;

  useEffect(() => {
    const updateCounts = () => {
      const selectedRows = props.api.getSelectedRows();
      
      const turnover = selectedRows.reduce((sum, row) => {
        if (row.is_excluded) return sum;
        return sum + (row.turnover || 0);
      }, 0);
      
      const billableHours = selectedRows.reduce((sum, row) => {
        return sum + (row.billable_hours || 0);
      }, 0);
      
      const travelTime = selectedRows.reduce((sum, row) => {
        return sum + (row.travel_time_billable || 0);
      }, 0);
      
      let hasTravelTime = false;
      if (includeTravelTime) {
        props.api.forEachNode((node) => {
          if (node.data && (node.data.travel_time_billable || 0) > 0) {
            hasTravelTime = true;
          }
        });
      }
      
      setSelectedTurnover(turnover);
      setSelectedBillableHours(billableHours);
      setSelectedTravelTime(travelTime);
      setShowTravelTime(hasTravelTime && includeTravelTime);
    };

    props.api.addEventListener('rowDataUpdated', updateCounts);
    props.api.addEventListener('selectionChanged', updateCounts);

    updateCounts();

    return () => {
      props.api.removeEventListener('rowDataUpdated', updateCounts);
      props.api.removeEventListener('selectionChanged', updateCounts);
    };
  }, [props.api, includeTravelTime]);

  return (
    <div className="ag-status-name-value" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <span>
        Total:
      </span>
      <span>
        <strong>Billable Hours:</strong> {selectedBillableHours.toFixed(2)}h
      </span>
      {showTravelTime && (
        <span>
          <strong>Travel Time:</strong> {selectedTravelTime.toFixed(2)}h
        </span>
      )}
      <span>
        <strong>Turnover:</strong> €{selectedTurnover.toFixed(2)}
      </span>
    </div>
  );
};

const StatusBarRightComponent: React.FC<CustomStatusPanelProps> = (props) => {
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCount, setSelectedCount] = useState(0);

  useEffect(() => {
    const updateCounts = () => {
      const total = props.api.getDisplayedRowCount();
      const selectedRows = props.api.getSelectedRows();
      const selected = selectedRows.length;
      
      setTotalCount(total);
      setSelectedCount(selected);
    };

    props.api.addEventListener('rowDataUpdated', updateCounts);
    props.api.addEventListener('selectionChanged', updateCounts);

    updateCounts();

    return () => {
      props.api.removeEventListener('rowDataUpdated', updateCounts);
      props.api.removeEventListener('selectionChanged', updateCounts);
    };
  }, [props.api]);

  return (
    <div className="ag-status-name-value" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <span>
        Selected: {selectedCount} out of {totalCount} Record(s)
      </span>
    </div>
  );
};

interface TreeRow {
  employee_id: string;
  employee_name: string;
  billable_hours: number;
  travel_time_billable: number;
  worktype?: string;
  sales_rate: number;
  turnover: number;
  is_excluded: boolean;
}

interface GroupReportTableProps {
  projectNumber: string;
  projectName: string;
  month: string;
  showPreview?: boolean;
  selectedOrder?: string;
  customOrderNumber?: string;
}

export const GroupReportTable: React.FC<GroupReportTableProps> = ({ projectNumber, projectName, month, showPreview = false, selectedOrder = '', customOrderNumber = '' }) => {
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<string>>(new Set());
  const hasInitialSelectionRef = useRef(false);
  const prevShowPreviewRef = useRef(showPreview);
  const { user } = useAuth();
  const userRole = user?.role?.toLowerCase().trim() || '';
  const isAdmin = userRole === 'admin';
  const canViewSalesRate = isAdmin || ['project manager', 'project_manager', 'assistant', 'deputy', 'finance'].includes(userRole);

  const getRowKey = useCallback((employeeId: string, worktype?: string, salesRate?: number): string => {
    const rate = salesRate ?? 0;
    return worktype != null || rate !== 0
      ? `${employeeId}_${worktype ?? ''}_${rate}`
      : employeeId;
  }, []);

  const {
    data: reportData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['customer-report-group', projectNumber, month, selectedOrder],
    queryFn: () => customerReportService.getGroupReport(projectNumber, month, selectedOrder),
    enabled: !!projectNumber && !!month,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const transformToTreeData = useCallback((employees: GroupReportEmployee[]): TreeRow[] => {
    return employees.map(emp => ({
      ...emp,
    }));
  }, []);

  const treeData = useMemo(() => {
    if (!reportData?.employees) return [];
    return transformToTreeData(reportData.employees);
  }, [reportData, transformToTreeData]);

  useEffect(() => {
    if (reportData?.employees) {
      const allSelected = new Set(reportData.employees.map((emp) => getRowKey(emp.employee_id, emp.worktype, emp.sales_rate)));
      setSelectedEmployeeIds(allSelected);
      hasInitialSelectionRef.current = false;
    }
  }, [reportData, getRowKey]);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const handleFirstDataRendered = useCallback(() => {
    if (gridApi && selectedEmployeeIds.size > 0 && !hasInitialSelectionRef.current) {
      gridApi.forEachNode((node) => {
        const data = node.data;
        const rowKey = data?.employee_id != null ? getRowKey(data.employee_id, data.worktype, data.sales_rate) : null;
        if (rowKey && selectedEmployeeIds.has(rowKey)) {
          node.setSelected(true, false);
        } else if (rowKey) {
          node.setSelected(false, false);
        }
      });
      hasInitialSelectionRef.current = true;
    }
  }, [gridApi, selectedEmployeeIds, getRowKey]);

  useEffect(() => {
    if (gridApi && treeData.length > 0 && selectedEmployeeIds.size > 0 && !hasInitialSelectionRef.current && !showPreview) {
      const timeoutId = setTimeout(() => {
        gridApi.forEachNode((node) => {
          const data = node.data;
          const rowKey = data?.employee_id != null ? getRowKey(data.employee_id, data.worktype, data.sales_rate) : null;
          if (rowKey && selectedEmployeeIds.has(rowKey)) {
            node.setSelected(true, false);
          } else if (rowKey) {
            node.setSelected(false, false);
          }
        });
        hasInitialSelectionRef.current = true;
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [gridApi, treeData, selectedEmployeeIds, showPreview, getRowKey]);

  const handleSelectionChanged = useCallback(() => {
    const api = gridRef.current?.api || gridApi;
    if (api) {
      const selectedNodes = api.getSelectedNodes();
      const newSelectedKeys = new Set<string>();

      selectedNodes.forEach(node => {
        const data = node.data;
        if (data?.employee_id != null) {
          newSelectedKeys.add(getRowKey(data.employee_id, data.worktype, data.sales_rate));
        }
      });

      api.forEachNode((node) => {
        const data = node.data;
        if (data?.employee_id != null) {
          const rowKey = getRowKey(data.employee_id, data.worktype, data.sales_rate);
          const shouldBeSelected = newSelectedKeys.has(rowKey);
          if (node.isSelected() !== shouldBeSelected) {
            node.setSelected(shouldBeSelected, false);
          }
        }
      });

      setSelectedEmployeeIds(newSelectedKeys);
    }
  }, [gridApi, getRowKey]);


  const WorktypeCellRenderer = useCallback((params: any) => {
    const worktype = params.value || 'N/A';
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.875rem',
          backgroundColor: worktype === 'office' ? colors.accent.blue + '20' : colors.accent.green + '20',
          color: worktype === 'office' ? colors.accent.blue : colors.accent.green,
        }}
      >
        {worktype.toUpperCase()}
      </span>
    );
  }, []);


  const columnDefs = useMemo<ColDef[]>(() => {
    const cols: ColDef[] = [
      {
        headerName: 'Employee',
        field: 'employee_name',
        minWidth: 150,
        headerClass: `${agGridCSSClasses.modernHeader} filterable`,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        suppressHeaderMenuButton: false,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Billable h',
        field: 'billable_hours',
        flex: 1,
        minWidth: 100,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return '0.00';
          return params.value.toFixed(2);
        },
        type: 'numericColumn',
      },
    ];

    if (reportData?.include_travel_time) {
      cols.push({
        headerName: 'Travel Time Billable',
        field: 'travel_time_billable',
        flex: 1,
        minWidth: 120,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return '0.00';
          return params.value.toFixed(2);
        },
        type: 'numericColumn',
      });
    }

    cols.push(
      {
        headerName: 'Worktype',
        field: 'worktype',
        flex: 1,
        minWidth: 100,
        headerClass: `${agGridCSSClasses.modernHeader} filterable`,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        suppressHeaderMenuButton: false,
        menuTabs: ['filterMenuTab'],
        cellRenderer: WorktypeCellRenderer,
      },
      {
        headerName: 'Sales Rate',
        field: 'sales_rate',
        flex: 1,
        minWidth: 100,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: 'agNumberColumnFilter',
        hide: !canViewSalesRate,
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return '€0.00';
          return `€${params.value.toFixed(2)}`;
        },
        type: 'numericColumn',
      },
      {
        headerName: 'Turnover',
        field: 'turnover',
        flex: 1,
        minWidth: 100,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          const rowKey = params.data?.employee_id != null ? getRowKey(params.data.employee_id, params.data.worktype, params.data.sales_rate) : null;
          const value = params.data?.is_excluded || !rowKey || !selectedEmployeeIds.has(rowKey)
            ? 0
            : params.value || 0;
          return `€${value.toFixed(2)}`;
        },
        cellStyle: (params: any) => {
          const rowKey = params.data?.employee_id != null ? getRowKey(params.data.employee_id, params.data.worktype, params.data.sales_rate) : null;
          if (params.data?.is_excluded || !rowKey || !selectedEmployeeIds.has(rowKey)) {
            return { color: colors.neutral.grey[400] } as any;
          }
          return null;
        },
        type: 'numericColumn',
      }
    );

    return cols;
  }, [reportData?.include_travel_time, canViewSalesRate, WorktypeCellRenderer, selectedEmployeeIds, getRowKey]);

  const selectedEmployees = useMemo(() => {
    if (!reportData?.employees) return [];
    return reportData.employees.filter((emp) => selectedEmployeeIds.has(getRowKey(emp.employee_id, emp.worktype, emp.sales_rate)));
  }, [reportData?.employees, selectedEmployeeIds, getRowKey]);

  const previewTotals = useMemo(() => {
    if (!selectedEmployees.length) {
      return {
        totalBillableHours: 0,
        totalTravelTimeBillable: 0,
        totalTurnover: 0,
      };
    }

    return {
      totalBillableHours: selectedEmployees.reduce((sum, emp) => sum + emp.billable_hours, 0),
      totalTravelTimeBillable: selectedEmployees.reduce((sum, emp) => sum + emp.travel_time_billable, 0),
      totalTurnover: selectedEmployees.reduce((sum, emp) => sum + (emp.is_excluded ? 0 : emp.turnover), 0),
    };
  }, [selectedEmployees]);

  const rowSelection = useMemo(() => {
    return {
      mode: 'multiRow' as const,
      headerCheckbox: true,
      checkboxes: true,
    };
  }, []);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: StatusBarComponent,
          statusPanelParams: {
            includeTravelTime: reportData?.include_travel_time ?? false,
          },
          key: 'statusBar',
          align: 'left',
        },
        {
          statusPanel: StatusBarRightComponent,
          key: 'statusBarRight',
          align: 'right',
        },
      ],
    };
  }, [reportData?.include_travel_time]);

  const gridOptions = {
    ...agGridDefaultOptions,
    theme: agGridTheme,
    suppressContextMenu: true,
    onGridReady: handleGridReady,
    onFirstDataRendered: handleFirstDataRendered,
    onSelectionChanged: handleSelectionChanged,
    defaultColDef: {
      ...agGridDefaultColDef,
      sortable: true,
    },
    getRowId: (params: any) => {
      const d = params.data;
      if (!d?.employee_id) return '';
      return getRowKey(d.employee_id, d.worktype, d.sales_rate);
    },
  };

  if (error) {
    return (
      <div
        style={{
          padding: '1rem',
          backgroundColor: colors.accent.red + '10',
          color: colors.accent.red,
          borderRadius: '6px',
        }}
      >
        Error loading report: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  useEffect(() => {
    const wasInPreview = prevShowPreviewRef.current;
    const isReturningFromPreview = wasInPreview && !showPreview;
    
    prevShowPreviewRef.current = showPreview;
    
    if (isReturningFromPreview && selectedEmployeeIds.size > 0) {
      hasInitialSelectionRef.current = false;
      
      const restoreSelection = () => {
        const api = gridRef.current?.api;
        if (api) {
          api.forEachNode((node) => {
            const data = node.data;
            const rowKey = data?.employee_id != null ? getRowKey(data.employee_id, data.worktype, data.sales_rate) : null;
            if (rowKey && selectedEmployeeIds.has(rowKey)) {
              node.setSelected(true, false);
            } else if (rowKey) {
              node.setSelected(false, false);
            }
          });
          hasInitialSelectionRef.current = true;
        } else {
          setTimeout(restoreSelection, 100);
        }
      };

      const timeoutId = setTimeout(restoreSelection, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [showPreview, selectedEmployeeIds, getRowKey]);

  return (
    <div style={{ height: 'calc(100dvh - 410px)', display: 'flex', flexDirection: 'column' }}>
      {!showPreview && (
        <>
          <style>{agGridThemeStyles}</style>
          <style>{`
            .ag-theme-quartz .ag-cell {
              display: flex;
              align-items: center;
              vertical-align: middle;
            }
            .ag-theme-quartz .ag-cell-wrapper {
              display: flex;
              align-items: center;
              width: 100%;
            }
            .ag-theme-quartz .ag-selection-checkbox {
              display: flex;
              align-items: center;
            }
            .ag-theme-quartz .ag-status-bar {
              width: 100%;
            }
            .ag-theme-quartz .ag-status-bar-panel {
              width: 100%;
            }
            .ag-theme-quartz .ag-header-cell.filterable .ag-header-cell-menu-button,
            .ag-theme-quartz .ag-header-cell[col-id="employee_name"] .ag-header-cell-menu-button,
            .ag-theme-quartz .ag-header-cell[col-id="worktype"] .ag-header-cell-menu-button {
              display: flex !important;
              opacity: 1 !important;
              visibility: visible !important;
            }
            .ag-theme-quartz .ag-header-cell.filterable .ag-header-cell-menu-button .ag-icon:not(.ag-icon-filter),
            .ag-theme-quartz .ag-header-cell[col-id="employee_name"] .ag-header-cell-menu-button .ag-icon:not(.ag-icon-filter),
            .ag-theme-quartz .ag-header-cell[col-id="worktype"] .ag-header-cell-menu-button .ag-icon:not(.ag-icon-filter) {
              display: none !important;
            }
            .ag-theme-quartz .ag-header-cell.filterable .ag-header-cell-menu-button .ag-icon-filter,
            .ag-theme-quartz .ag-header-cell[col-id="employee_name"] .ag-header-cell-menu-button .ag-icon-filter,
            .ag-theme-quartz .ag-header-cell[col-id="worktype"] .ag-header-cell-menu-button .ag-icon-filter,
            .ag-theme-quartz .ag-header-cell.filterable .ag-icon-filter {
              display: block !important;
            }
            .ag-theme-quartz .ag-header-cell.filterable .ag-header-cell-filter-button,
            .ag-theme-quartz .ag-header-cell[col-id="employee_name"] .ag-header-cell-filter-button,
            .ag-theme-quartz .ag-header-cell[col-id="worktype"] .ag-header-cell-filter-button {
              display: block !important;
            }
          `}</style>
          <div
            className="ag-theme-quartz"
            style={{
              ...agGridContainerStyles,
              flex: '1 1 auto',
              minHeight: '500px',
            }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={treeData}
              columnDefs={columnDefs}
              defaultColDef={agGridDefaultColDef}
              gridOptions={gridOptions}
              rowSelection={rowSelection}
              statusBar={statusBar}
              suppressRowClickSelection={false}
              animateRows={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
            />

            {isLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: colors.neutral.grey[600],
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  backgroundColor: colors.neutral.white,
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}
              >
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #e2e8f0',
                    borderTop: '2px solid #3b82f6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Loading report data...
              </div>
            )}
          </div>
        </>
      )}

      {showPreview && reportData && selectedEmployees.length > 0 && (
        <GroupReportPreview
          reportData={reportData}
          selectedEmployees={selectedEmployees}
          previewTotals={previewTotals}
          canViewSalesRate={canViewSalesRate}
          projectNumber={projectNumber}
          projectName={projectName}
          month={month}
          selectedOrder={selectedOrder}
          customOrderNumber={customOrderNumber}
        />
      )}
    </div>
  );
};
